<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useTranslation } from '@/utils/locales/messages';
import { baseCodeApi } from '@/shared/api/baseCodeApi';

const props = withDefaults(
  defineProps<{
    countryCode: string;
    areaCode: string;
    mobileNo: string;
    readonly?: boolean;
    required?: boolean;
    numberLabel?: string;
    /** "지역코드 없음" 체크박스 노출 */
    showAreaCodeToggle?: boolean;
    areaCodeNotApplicable?: boolean;
  }>(),
  {
    readonly: false,
    required: false,
  },
);

const emit = defineEmits<{
  'update:countryCode': [value: string];
  'update:areaCode': [value: string];
  'update:mobileNo': [value: string];
  'update:areaCodeNotApplicable': [value: boolean];
}>();

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

function onAreaCodeToggle(v: boolean | null) {
  // NA 플래그 하나만 emit. area code 비우기는 아래 watcher 가 다음 틱에 단일 emit 으로 처리한다.
  // (체크 시 여기서 update:areaCode 를 같이 emit 하면 부모 updateRow 가 stale modelValue 기준으로
  //  두 번 갱신하며 NA 변경을 덮어써 "체크 안 됨" 현상이 발생 → 단일 emit 으로 회피.)
  emit('update:areaCodeNotApplicable', !!v);
}

const countryCodeOptions = ref<
  Array<{ TOTAL_CODE: string; CODE_NAME: string; BIGO1?: string; BIGO3?: string }>
>([]);
const isLoadingCountryCodes = ref(false);

const countryCodeDisplayName = computed(() => {
  if (!props.countryCode) return '';
  const option = countryCodeOptions.value.find((opt) => opt.TOTAL_CODE === props.countryCode);
  return option?.CODE_NAME || props.countryCode;
});

/**
 * 선택된 국가번호가 싱가포르인지. countryCode 가 어떤 포맷으로 저장돼 있어도 감지:
 * 표시명("SINGAPORE +65") / dial("65"/"+65") / 정규 TOTAL_CODE / CODE_NAME 매칭 모두 커버.
 */
const isSingaporeDial = computed(() => {
  const cc = String(props.countryCode || '').trim();
  if (!cc) return false;
  // 값 자체가 표시명/이름이면 (예: "SINGAPORE +65", "Singapore")
  if (/singapore/i.test(cc)) return true;
  // 값이 dial 이면 (예: "65" / "+65")
  if (cc.replace(/^\+/, '') === '65') return true;

  const option =
    countryCodeOptions.value.find((opt) => opt.TOTAL_CODE === cc) ||
    countryCodeOptions.value.find((opt) => opt.CODE_NAME === cc);
  if (!option) return false;
  const b1 = String(option.BIGO1 || '').trim();
  const b3 = String(option.BIGO3 || '').trim();
  const dial = (/^\+?\d+$/.test(b1) ? b1 : /^\+?\d+$/.test(b3) ? b3 : b1 || b3).replace(/^\+/, '');
  if (dial === '65') return true;
  return /singapore/i.test(option.CODE_NAME || '');
});

/**
 * 들어온 countryCode 가 정규 TOTAL_CODE 가 아니고 dial(예: "65"/"+65") 이면,
 * 옵션의 dial(BIGO1/BIGO3) 로 매칭되는 항목을 찾아 TOTAL_CODE 로 자동 정규화한다.
 * (E-Filing 시드가 dial "65" 로 채우는 경우 → 드롭다운 표시/매칭/SG 숨김이 모두 정상 동작하도록.)
 * setupEntity 처럼 이미 TOTAL_CODE 로 저장된 값은 no-op.
 */
function normalizeCountryCode() {
  const v = String(props.countryCode || '').trim();
  if (!v || !countryCodeOptions.value.length) return;
  if (countryCodeOptions.value.some((o) => o.TOTAL_CODE === v)) return; // 이미 정규 코드

  // 1) 표시명(CODE_NAME) 으로 저장된 경우 (예: "SINGAPORE +65")
  const byName = countryCodeOptions.value.find((o) => o.CODE_NAME === v);
  if (byName) {
    if (byName.TOTAL_CODE !== v) emit('update:countryCode', byName.TOTAL_CODE);
    return;
  }

  // 2) dial 로 저장된 경우 (예: "65" / "+65")
  const digits = v.replace(/^\+/, '');
  if (!/^\d+$/.test(digits)) return;
  const byDial = countryCodeOptions.value.find((o) => {
    const b1 = String(o.BIGO1 || '').replace(/^\+/, '');
    const b3 = String(o.BIGO3 || '').replace(/^\+/, '');
    return b1 === digits || b3 === digits;
  });
  if (byDial && byDial.TOTAL_CODE !== v) emit('update:countryCode', byDial.TOTAL_CODE);
}
watch([() => props.countryCode, countryCodeOptions], normalizeCountryCode, { immediate: true });

// NA 체크 또는 SG(65) 선택 시 저장된 area code 를 비운다.
// watcher 는 source(na/sg) 변경이 반영된 다음 틱에 단일 emit → 부모 updateRow clobber 없이 데이터가 깨끗해진다.
watch(
  [() => props.areaCodeNotApplicable, isSingaporeDial],
  ([na, sg]) => {
    if ((na || sg) && props.areaCode) emit('update:areaCode', '');
  },
  { immediate: true },
);

const numberFieldLabel = computed(
  () => props.numberLabel || t('contact.Mobile No') || 'Mobile No.',
);

async function loadCountryCodes() {
  isLoadingCountryCodes.value = true;
  try {
    const response = await baseCodeApi.getCodesByMainCode('CM', 'B05', undefined);
    const codes = (response as any).codes || (response as any).data?.codes || [];

    const mapped = codes
      .map((code: any) => ({
        TOTAL_CODE: String(code.totalCode || code.TOTAL_CODE || '').trim(),
        CODE_NAME: String(
          code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
        ).trim(),
        BIGO1: String(code.bigo1 || code.BIGO1 || '').trim(),
        BIGO3: String(code.bigo3 || code.BIGO3 || '').trim(),
      }))
      .filter((item: { TOTAL_CODE: string; CODE_NAME: string }) => item.TOTAL_CODE && item.CODE_NAME);

    const uniqueCodesMap = new Map<
      string,
      { TOTAL_CODE: string; CODE_NAME: string; BIGO1?: string; BIGO3?: string }
    >();
    mapped.forEach((item: { TOTAL_CODE: string; CODE_NAME: string; BIGO1?: string; BIGO3?: string }) => {
      if (!uniqueCodesMap.has(item.TOTAL_CODE)) {
        uniqueCodesMap.set(item.TOTAL_CODE, item);
      }
    });

    countryCodeOptions.value = Array.from(uniqueCodesMap.values()).filter(
      (item) => item.TOTAL_CODE.slice(-3) !== '000',
    );
  } catch {
    countryCodeOptions.value = [];
  } finally {
    isLoadingCountryCodes.value = false;
  }
}

function updateCountryCode(val: string | null) {
  emit('update:countryCode', val || '');
}

onMounted(() => {
  loadCountryCodes();
});
</script>

<template>
  <v-row dense>
    <v-col cols="12" md="4">
      <v-text-field
        v-if="readonly"
        :model-value="countryCodeDisplayName"
        :label="t('contact.Country Code') || 'Country Code'"
        variant="outlined"
        density="comfortable"
        hide-details
        readonly
      />
      <v-autocomplete
        v-else
        :model-value="countryCode || null"
        :items="countryCodeOptions"
        item-title="CODE_NAME"
        item-value="TOTAL_CODE"
        :label="t('contact.Country Code') || 'Country Code'"
        :rules="required ? [(v: string) => !!v || t('setupEntity.sgIncorporation.validation.required')] : undefined"
        variant="outlined"
        density="comfortable"
        :placeholder="t('setupEntity.Please Select') || 'Please Select'"
        hide-details
        clearable
        :loading="isLoadingCountryCodes"
        :disabled="isLoadingCountryCodes"
        @update:model-value="updateCountryCode"
      />
    </v-col>
    <v-col v-if="!isSingaporeDial" cols="12" md="4">
      <v-text-field
        :model-value="areaCodeNotApplicable ? '' : areaCode"
        :label="t('contact.Area Code') || 'Area Code'"
        :readonly="readonly"
        :disabled="showAreaCodeToggle && areaCodeNotApplicable"
        variant="outlined"
        density="comfortable"
        :hide-details="showAreaCodeToggle && !readonly && !!String(countryCode || '').trim() ? 'auto' : true"
        :rules="showAreaCodeToggle && !readonly && !!String(countryCode || '').trim()
          ? [(v: string) => areaCodeNotApplicable || !!String(v || '').trim()
              || (t('setupEntity.sgIncorporation.contact.areaCodeRequired')
                  || 'Enter an area code, or tick \'no area code\' below')]
          : undefined"
        @update:model-value="emit('update:areaCode', $event)"
      />
      <v-checkbox
        v-if="showAreaCodeToggle"
        :model-value="!!areaCodeNotApplicable"
        :label="t('setupEntity.sgIncorporation.contact.noAreaCode')"
        :readonly="readonly"
        hide-details
        density="comfortable"
        @update:model-value="onAreaCodeToggle"
      />
    </v-col>
    <v-col cols="12" :md="isSingaporeDial ? 8 : 4">
      <v-text-field
        :model-value="mobileNo"
        :label="numberFieldLabel"
        :rules="required ? [(v: string) => !!v?.trim() || t('setupEntity.sgIncorporation.validation.required')] : undefined"
        :readonly="readonly"
        variant="outlined"
        density="comfortable"
        hide-details
        @update:model-value="emit('update:mobileNo', $event)"
      />
    </v-col>
  </v-row>
</template>
